import React, { useState } from "react";
import { GoogleLogin as OAuthLogin } from "@react-oauth/google";
import "./GoogleLogin.css";
import { analytics } from "../firebase/firebaseClient";
import { logEvent } from "firebase/analytics";

import { db } from "../firebase/firebaseClient";
import { doc, setDoc } from "firebase/firestore";
import { setSession } from "../utils/session";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/**
 * Helper to decode a JWT (Google credential) without external deps.
 * Returns parsed payload or null on error.
 *
 * NOTE: this is only ever used for non-trust-critical UI purposes (e.g.
 * showing a name/email while the login flow is still in progress). It is
 * NEVER used as the source of truth for the logged-in user's identity —
 * that always comes from the server-verified GET /api/auth/verify/ call.
 */
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.warn("Failed to parse JWT", e);
    return null;
  }
};

const logLoginToFirestore = (email) => {
  setTimeout(async () => {
    try {
      const loginTime = new Date().toISOString();
      const sessionId = Date.now().toString();

      await setDoc(doc(db, "user_logins", sessionId), {
        email,
        session_id: sessionId,
        login_time: loginTime,
      });

      logEvent(analytics, "user_login", {
        email,
        session_id: sessionId,
        login_time: loginTime,
      });
    } catch (e) {
      console.error("Background logging failed:", e);
    }
  }, 10);
};

const GoogleLogin = ({ onLoginSuccess, onLoginFailure }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null);

  const handleSuccess = async (response) => {
    setError(null);

    if (!response?.credential) {
      setError("Google did not return a credential. Please try again.");
      onLoginFailure?.(new Error("Missing Google credential"));
      return;
    }

    setIsVerifying(true);

    // Non-trust-critical: only used to have something to show before the
    // server-verified email comes back. Never used for setSession().
    const unverifiedPayload = parseJwt(response.credential);
    setPendingEmail(unverifiedPayload?.email || null);

    try {
      // 1. Get a CSRF token.
      const csrfRes = await fetch(`${API_BASE_URL}/api/auth/csrf-token/`);
      if (!csrfRes.ok) throw new Error("Failed to fetch CSRF token");
      const csrfData = await csrfRes.json();
      if (!csrfData?.success || !csrfData?.csrfToken) {
        throw new Error("Invalid CSRF token response");
      }

      // 2. Exchange the Google credential for our own session token.
      const loginRes = await fetch(`${API_BASE_URL}/api/google-login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credential: response.credential,
          csrfToken: csrfData.csrfToken,
        }),
      });
      if (!loginRes.ok) throw new Error("Google login failed");
      const loginData = await loginRes.json();
      if (!loginData?.success || !loginData?.token) {
        throw new Error("Invalid login response");
      }

      const token = loginData.token;

      // 3. Never trust any email in the login response body — verify the
      // token server-side to get the one true, trusted email.
      const verifyRes = await fetch(`${API_BASE_URL}/api/auth/verify/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!verifyRes.ok) throw new Error("Token verification failed");
      const verifyData = await verifyRes.json();
      const verifiedEmail = verifyData?.email;
      if (!verifiedEmail) throw new Error("No verified email returned");

      // 4. Persist the session using only the server-verified email.
      setSession({ email: verifiedEmail, token });

      // Keep existing login-logging call (best-effort, non-blocking).
      logLoginToFirestore(verifiedEmail);

      onLoginSuccess?.(response);
    } catch (err) {
      console.error(err);
      setError("We couldn't sign you in. Please try again.");
      onLoginFailure?.(err);
    } finally {
      setIsVerifying(false);
      setPendingEmail(null);
    }
  };

  const handleError = (err) => {
    setError("Google sign-in failed. Please try again.");
    // optional: log login failure to analytics
    if (typeof window !== "undefined" && analytics) {
      try {
        logEvent(analytics, "user_login_failure", {
          error: err?.toString?.() || "unknown_error",
          time: new Date().toISOString(),
        });
      } catch (e) {
        console.warn("Failed to log analytics event user_login_failure", e);
      }
    }
    if (typeof onLoginFailure === "function") onLoginFailure(err);
  };

  return (
    <div className="google-login-container">
      <div className="login-box">
        <div className="loginheader">
          <h1>Welcome to Haqdarshak</h1>
          <p className="loginsub-header">Every Citizen Matters.</p>
        </div>
        <div className="login-section">
          <p>Please Sign In</p>
          {isVerifying ? (
            <p className="login-status">
              {pendingEmail ? `Signing in as ${pendingEmail}…` : "Signing you in…"}
            </p>
          ) : (
            <OAuthLogin onSuccess={handleSuccess} onError={handleError} className="login-button" />
          )}
          {error && <p className="login-error">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default GoogleLogin;
